import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const PulseHeadline = styled.div`
  background: ${theme.colors.washi};
  border-radius: 20px;
  box-shadow: 
    0 4px 24px rgba(64, 145, 220, 0.05),
    0 1px 2px rgba(64, 145, 220, 0.03);
  display: flex;
  align-items: center;
  padding: 12px 20px;
  gap: 24px;
`;

const TitleGroup = styled.div`
  min-width: 200px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: ${theme.colors.sumi};
  font-family: 'M PLUS Rounded 1c', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
`;

const VersionTag = styled.span`
  background: #4091DC;
  color: ${theme.colors.washi};
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(64, 145, 220, 0.2);
`;

const Subtitle = styled.h2`
  font-size: 12px;
  color: ${theme.colors.sumi};
  font-weight: 400;
  opacity: 0.8;

  .highlight-managed {
    color: #4091DC;
    font-weight: 600;
  }

  .highlight-realtime {
    color: #40B8DC;
    font-weight: 600;
  }
`;

const ApiStats = styled.div`
  display: flex;
  gap: 20px;
  border-left: 1px solid rgba(64, 145, 220, 0.2);
  padding-left: 20px;
`;

const StatItem = styled.div`
  text-align: left;

  h3 {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 0;
    color: #4091DC;
    font-family: 'Space Mono', monospace;
  }

  p {
    font-size: 9px;
    color: ${theme.colors.sumi};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.6;
    font-weight: 500;
    margin: 0;
  }
`;

export function PulseHero() {
  const [stats, setStats] = useState<{
    active_agents: number;
    total_contexts: number;
    api_uptime: string;
  }>({
    active_agents: 0,
    total_contexts: 0,
    api_uptime: '0%'
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const fetchStats = async () => {
      if (!mountedRef.current) return;

      try {
        const response = await fetch('/api/pulse/stats');
        const data = await response.json();
        if (mountedRef.current) {
          setStats(data);
          setError(null);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError('Failed to load stats');
          console.error('Error fetching stats:', err);
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 15000); // Update every 15 seconds

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  return (
    <PulseHeadline>
      <TitleGroup>
        <Title>
          Pulse <VersionTag>v1.0</VersionTag>
        </Title>
        <Subtitle>
          A <span className="highlight-managed">fully managed</span>{' '}
          <span className="highlight-realtime">realtime</span> context API
        </Subtitle>
      </TitleGroup>
      <ApiStats>
        <StatItem>
          <h3>{loading ? '...' : error ? 'Error' : stats.active_agents.toLocaleString()}</h3>
          <p>Active Agents</p>
        </StatItem>
        <StatItem>
          <h3>{loading ? '...' : error ? 'Error' : stats.total_contexts.toLocaleString()}</h3>
          <p>Total Contexts</p>
        </StatItem>
        <StatItem>
          <h3>{loading ? '...' : error ? 'Error' : stats.api_uptime}</h3>
          <p>API Uptime</p>
        </StatItem>
      </ApiStats>
    </PulseHeadline>
  );
} 